import { formatLocalDate, ReservationType } from "src/utils/common";

class ReservationOverviewModel {
    constructor(item) {
        this.Id = item?.Id ?? null;
        this.ReservationNumber = item?.ReservationNumber ?? null;
        this.ReservationReference = item?.ReservationReference ?? null;
        this.ClientNetSell = item?.ClientSell ?? null;
        this.Status = item?.Status ?? null;
        this.Timestamp = item?.Timestamp ? formatLocalDate(item.Timestamp) : null;
        this.PaxCount = Number(item?.PaxCount) || 0;
        this.ClientCurrency = item?.ClientCurrency ?? null;
        this.ConfirmationNo = item?.ConfirmationNo ?? null;
        this.Pax = item?.Pax ?? null;
        this.AgentName = item?.Agent?.Name ?? null;
        this.UserName = item?.CreatedUser?.DisplayName ?? null;
        this.AgentId = item?.Agent?.Id ?? null;
        this.Hotels = this.initializeHotels(item);
        this.Services = this.initializeServices(item);
        this.fieldVisibility = this.determineFieldVisibility();
        this.Cost = this.getTotalCost();
        this.SelectionSectionTitle = this.getSelectionSectionTitle();
        this.ReservationType = this.getReservationType();
        this.PrimaryItem = this.getPrimaryItem();
        this.StartDate = item?.StartDate ? formatLocalDate(item?.StartDate) : null;
        this.EndDate = item?.EndDate ? formatLocalDate(item?.EndDate) : null;
        this.Provider = item?.Provider ?? null;
        this.CancellationNote = item?.CancellationNote ?? null;
    }

    initializeHotels(item) {
        return Array.isArray(item?.Hotels)
            ? item.Hotels.map(hotel => this.transformHotelData(hotel, item))
            : [];
    }

    initializeServices(item) {
        return Array.isArray(item?.Services)
            ? item.Services.map(service => this.transformServiceData(service, item))
            : [];
    }

    determineFieldVisibility() {
        const isServiceReservation = this.Services.length > 0;
    
    return {
        showCheckOut: !isServiceReservation && this.Hotels[0]?.CheckOut,
        showNights: !isServiceReservation && this.Hotels[0]?.CheckIn && this.Hotels[0]?.CheckOut,
    };
    }

    transformServiceData(service, item) {
        return {
            ServiceId: service.ServiceId ?? null,
            ServiceName: service.ServiceName ?? null,
            CheckIn: service.CheckIn ? formatLocalDate(service.CheckIn) : null,
            CheckOut: service.CheckOut ? formatLocalDate(service.CheckOut) : null,
            ClientNetSell: { Total: Number(service.ClientNetSell?.Total) || 0 },
            ContractCurrency: service.ContractCurrency ?? null,
            ContractBuy: { Total: Number(service.ContractBuy?.Total) || 0, },
            ExchangeRates: { 
                ContractToClient: Number(service.ExchangeRates?.ContractToClient) || 0, 
                ContractToSystem: Number(service.ExchangeRates?.ContractToSystem) || 0, 
                SystemToClient: Number(service.ExchangeRates?.SystemToClient) || 0, 
            },
            Discounts: service.Discounts || [],
            VoucherRef: null, // API will populate
            ConfirmationNo: service.SupplierConfirmation ?? null,
            Supplements: this.groupSupplements(service.Supplements || []),
            Units: this.getServiceUnit(service.Units, item),
            Status: service.Status ?? null,
            ItemReference: service.ItemReference ?? null,
            IsClientVoucherRaised: service.IsClientVoucherRaised,
            ServiceMessage: item.Restrictions || [],
        };
    }

    transformHotelData(hotel, item) {
        return {
            PropertyId: hotel.PropertyId ?? null,
            PropertyName: hotel.PropertyName ?? null,
            CheckIn: hotel.CheckIn ? formatLocalDate(hotel.CheckIn) : null,
            CheckOut: hotel.CheckOut ? formatLocalDate(hotel.CheckOut) : null,
            VoucherRef: null,  // API will populate
            ClientNetSell: { Total: Number(hotel.ClientNetSell?.Total) || 0, },
            ContractCurrency: hotel.ContractCurrency ?? null,
            ContractBuy: { Total: Number(hotel.ContractBuy?.Total) || 0, },
            ExchangeRates: { 
                ContractToClient: Number(hotel.ExchangeRates?.ContractToClient) || 0, 
                ContractToSystem: Number(hotel.ExchangeRates?.ContractToSystem) || 0, 
                SystemToClient: Number(hotel.ExchangeRates?.SystemToClient) || 0, 
            },
            ConfirmationNo: hotel.SupplierConfirmation ?? null,
            IsClientVoucherRaised: hotel.IsClientVoucherRaised,
            Rooms: this.getUniqueRooms(hotel.Rooms, item),
            Status: hotel.Status ?? null,
            ItemReference: hotel.ItemReference ?? null,
            HotelMessage: hotel.HotelRestrictions || [],
        };
    }

    getUniqueRooms(rooms, item) {
        return (rooms || []).map(room => ({
            ...room,
            HotelMessage: this.initializeMessages(item, room.Restrictions || []),
            Supplements: this.groupSupplements(room.Supplements || []),
        }));
    }

    getServiceUnit(unit, item) {
        return (unit || []).map(unit => ({
            ...unit,
            ServiceMessage: this.initializeMessages(item, unit.Restrictions || []),
            Supplements: this.groupSupplements(unit.Supplements || []),
        }));
    }

    groupSupplements(supplements) {
        const supplementMap = {};

        (supplements || []).forEach(supplement => {
            if (!supplement?.SupplementId) return;

            if (!supplementMap[supplement.SupplementId]) {
                supplementMap[supplement.SupplementId] = {
                    ...supplement,
                    Count: 1,
                };
            } else {
                supplementMap[supplement.SupplementId].Count += 1;
            }
        });

        return Object.values(supplementMap);
    }

    initializeMessages(item, roomRestrictions) {
        const messages = [];

        if (item?.Restrictions && roomRestrictions) {
            roomRestrictions.forEach((restrictionId) => {
                const matchedRestriction = item.Restrictions.find((r) => r.Id === restrictionId);
                if (matchedRestriction) {
                    messages.push(matchedRestriction);
                }
            });
        }

        return messages;
    }

    getTotalCost() {
        if (this.Hotels?.length > 0) {
            return {
                currency: this.ClientCurrency,
                amount: this.Hotels[0]?.ClientNetSell?.Total || 0
            };
        }

        if (this.Services?.length > 0) {
            return {
                currency: this.ClientCurrency,
                amount: this.Services[0]?.ClientNetSell?.Total || 0
            };
        }
        return {
            currency: this.ClientCurrency,
            amount: 0
        };
    }

    getReservationType() {
        if (this.Hotels?.length > 0) {
            return ReservationType.hotel;
        }
        if (this.Services?.length > 0) {
            return ReservationType.service;
        }
        return null;
    }

    getSelectionSectionTitle() {
        switch (this.getReservationType()) {
            case ReservationType.hotel:
                return 'Selected Rooms';
            case ReservationType.service:
                return 'Selected Services';
            default:
                return 'Selected Items';
        }
    }

    getPrimaryItem() {
        switch(this.getReservationType()) {
            case ReservationType.hotel:
                return this.Hotels?.[0];
            case ReservationType.service:
                return this.Services?.[0];
            default:
                return null;
        }
    }
}

export default ReservationOverviewModel;